import { Data2, useDataTypesHooks } from "../../../features/data-types"
import { useOperationBuilders, usePricesHooks } from "../../operations"
import { usePurchaseBuilders } from "../builders/purchaseBuilders"
import { TableName } from "../../../tables/types/types"
import { usePurchaseActions } from "../actions/purchaseActions"
import { usePurchaseLoaders } from "../loaders/purchaseLoaders"
import { firstItem } from "../../../utils/listUtils"
import { Ticket, useTicketService } from "../../administration"
import { fileHelpers } from "../../../features/files"
import { useDeliveryOrderActions, useDeliveryOrderLoaders, useDeliveryOrderBuilders } from "../../deliveries"

export const usePurchaseService = () => {
    const { purchaseTicketSubPath, getFileExtension } = fileHelpers
    const { generateCode } = useDataTypesHooks()
    const pricesHooks = usePricesHooks()

    const { buildPurchase } = usePurchaseBuilders()
    const { buildItem } = useOperationBuilders()
    const { buildDeliveryOrder, buildDeliveryOrderItem } = useDeliveryOrderBuilders()

    const purchaseLoaders = usePurchaseLoaders()
    const deliveryOrderLoaders = useDeliveryOrderLoaders()

    const { createTicket, updateTicket } = useTicketService()
    const purchaseActions = usePurchaseActions()
    const { saveDeliveryOrder } = useDeliveryOrderActions()

    const buildPurchaseItems = (itemsData: Data2[]) => {
        const items = itemsData.map(itemData => {
            const vatTaxYield = pricesHooks.product.vatTaxYield(itemData.productId! as string)
            return buildItem({
                ...itemData,
                vatTaxYield
            })
        })
        return items
    }

    const createPurchase = async (
        purchaseData: Data2,
        itemsData: Data2[]
    ) => {
        const code = await generateCode(TableName.PURCHASE_ORDERS)
        const items = buildPurchaseItems(itemsData)
        const purchase = buildPurchase({
            ...purchaseData,
            code,
            items
        })

        return purchaseActions.savePurchase(purchase)
    }

    const updatePurchase = async (
        purchaseData: Data2,
        itemsData: Data2[]
    ) => {
        const { loadPurchase } = purchaseLoaders()
        const loadedPurchase = (await loadPurchase(purchaseData.id! as string))!
        const { loadOperationDeliveryOrders } = deliveryOrderLoaders()
        const loadedDeliveryOrders = await loadOperationDeliveryOrders(loadedPurchase.id)
        const loadedDeliveryOrder = firstItem(loadedDeliveryOrders)!

        const items = buildPurchaseItems(itemsData)
        const purchase = buildPurchase({
            ...loadedPurchase,
            ...purchaseData,
            items
        })

        const deliveryOrderItems = items.map(item => buildDeliveryOrderItem({
            itemId: item.id,
            quantity: item.quantity
        }))
        const deliveryOrder = buildDeliveryOrder({
            ...loadedDeliveryOrder,
            items: deliveryOrderItems
        })
        await saveDeliveryOrder(deliveryOrder)
        
        return purchaseActions.savePurchase(purchase)
    }

    const archivePurchase = async (purchaseId: string) => {
        const { loadPurchase } = purchaseLoaders()
        const purchase = await loadPurchase(purchaseId, true)
        const archivedPurchase = buildPurchase({
            ...purchase,
            archived: true
        })
        return purchaseActions.savePurchase(archivedPurchase)
    }

    const createPurchaseTicket = async (
        purchaseId: string,
        ticketData: Data2,
        taxItemsData: Data2[] = []
    ) => {
        const { loadPurchase } = purchaseLoaders()
        const loadedPurchase = await loadPurchase(purchaseId)

        const newTicketData: Data2 = {
            ...ticketData,
            parentTable: TableName.PURCHASE_ORDERS,
            parentId: purchaseId
        }
        const ext = ticketData.ticketFile instanceof File ? getFileExtension(ticketData.ticketFile) : 'pdf'
        const fileSubPath = (ticket: Ticket) => purchaseTicketSubPath(ticket, loadedPurchase!.code, ext)
        await createTicket(newTicketData, fileSubPath, taxItemsData)
    }

    const updatePurchaseTicket = async (
        purchaseId: string,
        ticketData: Data2,
        taxItemsData: Data2[] = []
    ) => {
        const { loadPurchase } = purchaseLoaders()
        const loadedPurchase = await loadPurchase(purchaseId)

        const ext = ticketData.ticketFile instanceof File ? getFileExtension(ticketData.ticketFile) : 'pdf'
        const fileSubPath = (ticket: Ticket) => purchaseTicketSubPath(ticket, loadedPurchase!.code, ext)
        await updateTicket(ticketData, fileSubPath, taxItemsData)
    }

    return {
        createPurchase,
        updatePurchase,
        archivePurchase,
        createPurchaseTicket,
        updatePurchaseTicket
    }
}
